import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { DOCTORS, ASSETS } from "@/data/site";
import { useT } from "@/i18n/LanguageContext";
import { localizeDoctor } from "@/i18n/data.es";
import SunDivider from "./SunDivider";

export default function About() {
  const { t, lang } = useT();
  return (
    <section
      id="about"
      data-testid="about-section"
      className="px-6 md:px-10 lg:px-16 py-24 md:py-32"
    >
      <div className="text-center mb-14">
        <div className="font-mono-eyebrow text-[var(--text-muted)]">
          Meet your dentists
        </div>
        <h2 className="font-serif-display text-[var(--primary)] mt-4 text-[44px] md:text-[64px] leading-[1.02] tracking-tight">
          Discover dental{" "}
          <span className="italic text-[var(--accent)]">expertise</span>.
        </h2>
        <SunDivider className="mt-5" />
      </div>

      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="lg:sticky lg:top-28"
          >
            <div className="arch-shape aspect-[3/4] w-full max-w-[400px] mx-auto bg-[var(--surface)]">
              <img
                src={ASSETS.drPortrait}
                alt="Dr. Steve Yopa, founding dentist"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <p className="mt-6 text-[var(--text-muted)] text-[16px] leading-relaxed max-w-md mx-auto text-center">
              {t("Three doctors. Four languages. One belief — that great dentistry starts with great conversation. We slow down so your treatment speeds up.")}
            </p>
          </motion.div>
        </div>

        <div className="lg:col-span-7 flex flex-col gap-5">
          {DOCTORS.map((doctor, idx) => {
            const d = localizeDoctor(doctor, lang);
            return (
            <motion.article
              key={d.name}
              data-testid={`doctor-card-${idx}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: idx * 0.08 }}
              className="group bg-[var(--surface)] border border-[var(--border)] rounded-[28px] p-7 md:p-9 hover:shadow-[0_20px_60px_rgb(0,0,0,0.06)] hover:-translate-y-0.5 transition-all"
            >
              <div className="flex items-start justify-between gap-6">
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-[var(--accent)]">
                    {String(idx + 1).padStart(2, "0")} · {t(d.title)}
                  </div>
                  <h3 className="font-serif-display text-[34px] md:text-[40px] leading-tight mt-3 text-[var(--primary)]">
                    {d.name}
                  </h3>
                  <p className="mt-2 text-sm text-[var(--text-muted)]">
                    {t(d.title)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full border border-[var(--border)] grid place-content-center text-[var(--primary)] group-hover:bg-[var(--accent)] group-hover:text-white group-hover:border-[var(--accent)]">
                  <ArrowUpRight className="w-5 h-5" />
                </div>
              </div>
              <div className="mt-6 space-y-4 text-[15.5px] text-[var(--text-main)]/80 leading-relaxed max-w-2xl">
                {(Array.isArray(d.bio) ? d.bio : [d.bio]).map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
